// The Offer Negotiation card (DC-202, CR-032): the dated facts BRD §3.20 asks for — verbal and written dates, the
// counter or request, approval, the candidate's decision, the accepted and declined dates — as KeyValue rows, and the
// candidate's notes per round, tagged Internal. Notes are drawn only for a seat that reads them: the Coordinator's
// projection carries the chain and the dates and no note (D7), and the card never asks for one either.

import type { ReactNode } from "react";
import { Card, KeyValue, OFFER_STATUS, SCREEN_STATUS, Tag, TAG_LABELS, formatDate, type KeyValueItem } from "@/mc";
import { OFFER_TAB_LABELS, OFFER_RULE_LABELS } from "./labels";
import type { OfferView, Roles } from "./types";

const EMPTY = "—";

/** The roles that read the internal notes: the owning file's people, never the Coordinator or an interviewer. */
const NOTE_READERS = new Set(["recruiter", "hm", "hrbp", "ta_manager", "compensation", "admin"]);

export function seesNotes(roles: Roles): boolean {
  return roles.some((role) => NOTE_READERS.has(role));
}

const day = (at: string | null | undefined): string => (at ? formatDate(at) : EMPTY);

/** The candidate's decision in his words (OFFER chain terminal words; a withdrawal is Step 8's Withdrawn). */
export function decisionWord(offer: OfferView): string {
  const kind = offer.decision?.kind ?? offer.status;
  if (kind === "accepted") return OFFER_STATUS.accepted;
  if (kind === "declined") return OFFER_STATUS.declined;
  if (kind === "withdrawn") return SCREEN_STATUS.withdrawn;
  return EMPTY;
}

/** The latest counter or request, as the candidate put it (field values are data). */
export function counterText(offer: OfferView): string {
  const rounds = offer.negotiation ?? [];
  const latest = rounds.length ? [...rounds].sort((a, b) => b.round_no - a.round_no)[0] : undefined;
  if (!latest) return EMPTY;
  const parts = latest.requested.map((item) => item.text ?? (item.value === null || item.value === undefined ? "" : String(item.value))).filter(Boolean);
  return parts.length ? parts.join(" · ") : EMPTY;
}

function stepAt(offer: OfferView, key: string): string | null {
  return offer.chain?.steps.find((step) => step.key === key)?.at ?? null;
}

export function negotiationRows(offer: OfferView): KeyValueItem[] {
  const fields = offer.fields as Record<string, string | null | undefined>;
  const decided = offer.decision;
  const acceptedAt = decided?.kind === "accepted" ? decided.decided_at : fields.accepted_at;
  const declinedAt = decided?.kind === "declined" ? decided.decided_at : null;
  return [
    [OFFER_STATUS.verbalExtended, day(fields.verbal_extended_at ?? stepAt(offer, "verbal_extended"))],
    [OFFER_STATUS.writtenSent, day(fields.letter_sent_at ?? stepAt(offer, "written_sent"))],
    [OFFER_TAB_LABELS.counterRequest, counterText(offer)],
    [OFFER_TAB_LABELS.approval, day(stepAt(offer, "approved"))],
    [OFFER_TAB_LABELS.candidateDecision, decisionWord(offer)],
    [OFFER_TAB_LABELS.acceptedDate, day(acceptedAt)],
    [OFFER_STATUS.declined, day(declinedAt)],
  ];
}

export interface NegotiationCardProps {
  offer: OfferView;
  roles: Roles;
  actions?: ReactNode;
}

export function NegotiationCard({ offer, roles, actions }: NegotiationCardProps) {
  const notes = seesNotes(roles)
    ? (offer.negotiation ?? []).filter((round) => !!round.candidate_note).sort((a, b) => a.round_no - b.round_no)
    : [];
  return (
    <Card title={OFFER_TAB_LABELS.offerNegotiation} actions={actions}>
      <KeyValue items={negotiationRows(offer)} cols={3} />
      {notes.length > 0 ? (
        <div className="mc-offer-notes" data-offer-notes="">
          {notes.map((round) => (
            <p key={round.id ?? round.round_no} className="mc-offer-note">
              <Tag tone="internal">{TAG_LABELS.internal}</Tag>
              <span>{day(round.received_at)}</span>
              <span>{round.candidate_note}</span>
            </p>
          ))}
          <p className="mc-offer-note">{OFFER_RULE_LABELS.noInternalNotes}</p>
        </div>
      ) : null}
    </Card>
  );
}
